import { useState } from "react";

import { closeIcon } from "../../../assetData/icons";
import BackDrop from "../../ui/BackDrop";
import MirageLoader from "../../ui/pageLoader/MirageLoader";
import { getTemplateFormat, getTemplateId, getTemplateName } from "./templatePmsUtils";

const getNextTemplateId = (templates = []) => {
  const maxId = templates.reduce((max, template) => {
    const id = Number(getTemplateId(template));
    return Number.isFinite(id) && id > max ? id : max;
  }, 0);
  return maxId + 1;
};

const TemplatePMSForm = ({ data, templates = [], onClose, onSubmit }) => {
  const isEdit = Boolean(getTemplateId(data));
  const [form, setForm] = useState({
    template_id: getTemplateId(data) || getNextTemplateId(templates),
    name: getTemplateName(data),
    format: getTemplateFormat(data) || "HTML",
    status: data?.status === false || data?.status === 0 || data?.status === "0" ? 0 : 1,
    template_image: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [nameError, setNameError] = useState("");

  const isDuplicateName = (name) => templates.some((template) =>
    String(getTemplateName(template)).trim().toLowerCase() === name.trim().toLowerCase() &&
    String(getTemplateId(template)) !== String(getTemplateId(data)),
  );

  const submit = async (event) => {
    event.preventDefault();
    const nameExists = isDuplicateName(form.name);
    if (nameExists) {
      setNameError("Template name already exists.");
      return;
    }

    const payload = new FormData();
    if (isEdit) payload.append("id", getTemplateId(data));
    payload.append("template_id", form.template_id);
    payload.append("name", form.name);
    payload.append("format", form.format);
    payload.append("status", String(form.status ? 1 : 0));
    if (form.template_image) payload.append("template_image", form.template_image);

    setSubmitting(true);
    const saved = await onSubmit(payload, isEdit);
    setSubmitting(false);
    if (saved) onClose();
  };

  return (
    <>
      <div className="fixed left-1/2 top-1/2 z-50 w-[95%] max-w-[700px] -translate-x-1/2 -translate-y-1/2 rounded bg-white p-8">
        <h2 className="pb-6 text-2xl font-bold text-neutral-800">
          {isEdit ? "Update PMS Template" : "Create PMS Template"}
        </h2>
        <button type="button" className="absolute right-8 top-8" onClick={onClose}>
          <img src={closeIcon} alt="Close" />
        </button>
        <form onSubmit={submit}>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <label className="text-[#636363]">Template ID*</label>
              <input required type="number" value={form.template_id} onChange={(event) => setForm({ ...form, template_id: event.target.value })} className="rounded bg-neutral-100 p-2 text-[#636363] focus:outline-none" />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[#636363]">Template Name*</label>
              <input required value={form.name} onChange={(event) => {
                const value = event.target.value;
                setNameError(isDuplicateName(value) ? "Template name already exists." : "");
                setForm({ ...form, name: value });
              }} className="rounded bg-neutral-100 p-2 text-[#636363] focus:outline-none" />
              {nameError && <p className="text-sm text-red-500">{nameError}</p>}
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[#636363]">Format*</label>
              <select value={form.format} onChange={(event) => setForm({ ...form, format: event.target.value })} className="rounded bg-neutral-100 p-2 text-[#636363] focus:outline-none">
                <option value="HTML">HTML</option>
                <option value="PDF">PDF</option>
                <option value="Multiple PDF">Multiple PDF</option>
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[#636363]">Status*</label>
              <select value={String(form.status)} onChange={(event) => setForm({ ...form, status: Number(event.target.value) })} className="rounded bg-neutral-100 p-2 text-[#636363] focus:outline-none">
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[#636363]">Template Image{isEdit ? "" : "*"}</label>
              <input type="file" accept="image/*" required={!isEdit} onChange={(event) => setForm({ ...form, template_image: event.target.files?.[0] })} className="rounded bg-neutral-100 p-2 text-[#636363]" />
            </div>
          </div>
          <button disabled={submitting || Boolean(nameError)} className="mt-8 w-full rounded border-2 border-pri-clr-orange bg-pri-clr-orange px-12 py-2 font-semibold text-white sm:w-1/3">
            {submitting ? <MirageLoader /> : isEdit ? "UPDATE" : "CREATE"}
          </button>
        </form>
      </div>
      <BackDrop closeHandler={onClose} />
    </>
  );
};

export default TemplatePMSForm;
